/**
 * VehicleExitSuccessModal - Shows TCO summary after vehicle exit completion
 *
 * Displays:
 * - Exit confirmation
 * - TCO breakdown summary
 * - Download options (PDF, CSV)
 */

import { useOrgCurrency } from '../../../hooks/useOrgCurrency.js';
import { formatDate as fmtDate, formatNumber as fmtNumber, formatDateTime as fmtDateTime } from '../../../i18n/format.js';
import React, { useState, useEffect } from 'react';
import { getAuthHeaders } from '../../../api/core.js';
import {
  CheckCircle,
  X,
  Download,
  FileText,
  TrendingUp,
  Car,
  Calendar,
  Fuel,
  Wrench,
  AlertTriangle,
  Receipt,
  Shield,
  DollarSign,
  Eye,
  Loader2
} from 'lucide-react';

const API_BASE = '/api/v1';



// Format number
const formatNumber = (num) => {
  if (num == null) return '-';
  return fmtNumber(num);
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return fmtDate(dateStr);
};

export default function VehicleExitSuccessModal({
  isOpen,
  onClose,
  vehicle,
  exitData,
  archiveId
}) {
  // Devise de l'organisation du vehicule - un seul outil monetaire.
  const { money } = useOrgCurrency();
  const formatCurrency = (amount) =>
    (amount == null || isNaN(amount) ? '-' : money(amount, vehicle));

  const [tcoReport, setTcoReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch TCO report when modal opens
  useEffect(() => {
    if (isOpen && archiveId) {
      fetchTcoReport();
    }
  }, [isOpen, archiveId]);

  const fetchTcoReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/vehicle-archives/${archiveId}/tco-report`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (data.ok) {
        setTcoReport(data.data);
      } else {
        setError('Impossible de charger le rapport TCO');
      }
    } catch (err) {
      console.error('Error fetching TCO report:', err);
      setError('Erreur lors du chargement du rapport');
    } finally {
      setLoading(false);
    }
  };

  // Download TCO report as CSV
  const downloadCSV = () => {
    if (!tcoReport) return;

    const rows = [
      ['RAPPORT TCO - COUT TOTAL DE POSSESSION'],
      [],
      ['VEHICULE'],
      ['Marque', tcoReport.vehicle?.make || ''],
      ['Modèle', tcoReport.vehicle?.model || ''],
      ['Année', tcoReport.vehicle?.year || ''],
      ['Immatriculation', tcoReport.vehicle?.license_plate || ''],
      ['VIN', tcoReport.vehicle?.vin || ''],
      [],
      ['PRESENCE DANS LA FLOTTE'],
      ['Date d\'acquisition', tcoReport.presence?.acquisition_date || ''],
      ['Date de sortie', tcoReport.presence?.disposal_date || ''],
      ['Duree (mois)', tcoReport.presence?.months || 0],
      ['Km entree', tcoReport.presence?.entry_mileage || 0],
      ['Km sortie', tcoReport.presence?.exit_mileage || 0],
      ['Total parcouru (km)', tcoReport.presence?.total_km || 0],
      ['Moyenne km/mois', tcoReport.presence?.avg_km_per_month || 0],
      [],
      ['REPARTITION DES COUTS'],
      ['Categorie', 'Montant EUR'],
      ['Financement', tcoReport.tco?.breakdown?.acquisition?.total_payments || 0],
      ['Maintenance', tcoReport.tco?.breakdown?.maintenance?.total || 0],
      ['Carburant', tcoReport.tco?.breakdown?.fuel?.total || 0],
      ['Assurance', tcoReport.tco?.breakdown?.insurance?.total || 0],
      ['Sinistres', tcoReport.tco?.breakdown?.claims?.total || 0],
      ['Amendes', tcoReport.tco?.breakdown?.fines?.total || 0],
      [],
      ['SYNTHESE TCO'],
      ['TCO Total', tcoReport.tco?.totals?.gross_tco || 0],
      ['Valeur recuperee', tcoReport.tco?.totals?.disposal_recovery || 0],
      ['TCO Net', tcoReport.tco?.totals?.net_tco || 0],
      ['TCO par mois', tcoReport.tco?.totals?.per_month || 0],
      ['TCO par km', tcoReport.tco?.totals?.per_km || 0],
      [],
      ['SORTIE'],
      ['Motif', tcoReport.exit?.reason || ''],
      ['Detail', tcoReport.exit?.reason_detail || ''],
      ['Valeur de sortie', tcoReport.exit?.disposal_value || 0],
      [],
      ['Genere le', fmtDateTime(new Date())]
    ];

    const csvContent = rows.map(row => row.join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `TCO_${vehicle?.license_plate || 'vehicule'}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Generate printable HTML report
  const printReport = () => {
    if (!tcoReport) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Rapport TCO - ${vehicle?.license_plate || 'Vehicule'}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
          h1 { color: #1e40af; border-bottom: 2px solid #1e40af; padding-bottom: 10px; }
          h2 { color: #374151; margin-top: 24px; }
          .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin: 16px 0; }
          .card { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 16px; }
          .label { color: #6b7280; font-size: 12px; margin-bottom: 4px; }
          .value { font-size: 18px; font-weight: bold; }
          .total { background: #1e40af; color: white; }
          table { width: 100%; border-collapse: collapse; margin: 16px 0; }
          th, td { border: 1px solid #e5e7eb; padding: 8px; text-align: left; }
          th { background: #f3f4f6; }
          .footer { margin-top: 32px; font-size: 12px; color: #9ca3af; text-align: center; }
        </style>
      </head>
      <body>
        <h1>Rapport TCO - Cout Total de Possession</h1>

        <h2>Vehicule</h2>
        <div class="grid">
          <div class="card">
            <div class="label">Identification</div>
            <div class="value">${tcoReport.vehicle?.make} ${tcoReport.vehicle?.model}</div>
            <div>${tcoReport.vehicle?.license_plate} - ${tcoReport.vehicle?.year || ''}</div>
          </div>
          <div class="card">
            <div class="label">VIN</div>
            <div>${tcoReport.vehicle?.vin || '-'}</div>
          </div>
        </div>

        <h2>Presence dans la flotte</h2>
        <div class="grid">
          <div class="card">
            <div class="label">Periode</div>
            <div class="value">${tcoReport.presence?.months || 0} mois</div>
            <div>${formatDate(tcoReport.presence?.acquisition_date)} - ${formatDate(tcoReport.presence?.disposal_date)}</div>
          </div>
          <div class="card">
            <div class="label">Kilométrage</div>
            <div class="value">${formatNumber(tcoReport.presence?.total_km)} km</div>
            <div>${formatNumber(tcoReport.presence?.entry_mileage)} - ${formatNumber(tcoReport.presence?.exit_mileage)} km</div>
          </div>
        </div>

        <h2>Repartition des couts</h2>
        <table>
          <tr><th>Categorie</th><th>Montant</th></tr>
          <tr><td>Financement</td><td>${formatCurrency(tcoReport.tco?.breakdown?.acquisition?.total_payments)}</td></tr>
          <tr><td>Maintenance (${tcoReport.tco?.breakdown?.maintenance?.count || 0} interventions)</td><td>${formatCurrency(tcoReport.tco?.breakdown?.maintenance?.total)}</td></tr>
          <tr><td>Carburant (${formatNumber(tcoReport.tco?.breakdown?.fuel?.liters)} L)</td><td>${formatCurrency(tcoReport.tco?.breakdown?.fuel?.total)}</td></tr>
          <tr><td>Assurance (${tcoReport.tco?.breakdown?.insurance?.count || 0} contrats)</td><td>${formatCurrency(tcoReport.tco?.breakdown?.insurance?.total)}</td></tr>
          <tr><td>Sinistres (${tcoReport.tco?.breakdown?.claims?.count || 0})</td><td>${formatCurrency(tcoReport.tco?.breakdown?.claims?.total)}</td></tr>
          <tr><td>Amendes (${tcoReport.tco?.breakdown?.fines?.count || 0})</td><td>${formatCurrency(tcoReport.tco?.breakdown?.fines?.total)}</td></tr>
        </table>

        <h2>Synthese TCO</h2>
        <div class="grid">
          <div class="card total">
            <div class="label" style="color:#93c5fd">TCO Total</div>
            <div class="value">${formatCurrency(tcoReport.tco?.totals?.gross_tco)}</div>
          </div>
          <div class="card">
            <div class="label">TCO Net (apres recuperation)</div>
            <div class="value">${formatCurrency(tcoReport.tco?.totals?.net_tco)}</div>
          </div>
          <div class="card">
            <div class="label">TCO par mois</div>
            <div class="value">${formatCurrency(tcoReport.tco?.totals?.per_month)}</div>
          </div>
          <div class="card">
            <div class="label">TCO par km</div>
            <div class="value">${(tcoReport.tco?.totals?.per_km || 0).toFixed(3)} EUR</div>
          </div>
        </div>

        <div class="footer">
          Rapport genere le ${fmtDateTime(new Date())} | FleetManager
        </div>

        <script>window.print();</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-4 rounded-t-xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-8 h-8" />
            <div>
              <h2 className="text-xl font-bold">Sortie Finalisee</h2>
              <p className="text-green-100 text-sm">Le vehicule a ete retire de la flotte</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Vehicle Info */}
          <div className="flex items-center gap-4 mb-6 pb-6 border-b">
            <div className="p-3 bg-slate-100 rounded-lg">
              <Car className="w-8 h-8 text-slate-600" />
            </div>
            <div>
              <div className="text-xl font-bold text-gray-900">
                {vehicle?.make} {vehicle?.model}
              </div>
              <div className="text-gray-500">{vehicle?.license_plate}</div>
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              <span className="ml-3 text-gray-600">Chargement du rapport TCO...</span>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4">
              {error}
            </div>
          )}

          {/* TCO Summary */}
          {tcoReport && !loading && (
            <>
              {/* TCO Header */}
              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-blue-600" />
                    <span className="font-medium text-blue-900">Cout Total de Possession (TCO)</span>
                  </div>
                  <div className="text-2xl font-bold text-blue-700">
                    {formatCurrency(tcoReport.tco?.totals?.gross_tco)}
                  </div>
                </div>
              </div>

              {/* Presence Summary */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                    <Calendar className="w-4 h-4" />
                    Presence
                  </div>
                  <div className="text-lg font-bold">{tcoReport.presence?.months || 0} mois</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                    <Car className="w-4 h-4" />
                    Kilométrage
                  </div>
                  <div className="text-lg font-bold">{formatNumber(tcoReport.presence?.total_km)} km</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                    <DollarSign className="w-4 h-4" />
                    TCO/km
                  </div>
                  <div className="text-lg font-bold">{(tcoReport.tco?.totals?.per_km || 0).toFixed(3)} EUR</div>
                </div>
              </div>

              {/* Cost Breakdown */}
              <div className="space-y-2 mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Repartition des couts</h3>

                {/* Financing */}
                {tcoReport.tco?.breakdown?.acquisition?.total_payments > 0 && (
                  <div className="flex items-center justify-between py-2 border-b">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-gray-400" />
                      <span>Financement ({tcoReport.presence?.months} mois)</span>
                    </div>
                    <span className="font-medium">{formatCurrency(tcoReport.tco?.breakdown?.acquisition?.total_payments)}</span>
                  </div>
                )}

                {/* Maintenance */}
                <div className="flex items-center justify-between py-2 border-b">
                  <div className="flex items-center gap-2">
                    <Wrench className="w-4 h-4 text-blue-500" />
                    <span>Maintenance ({tcoReport.tco?.breakdown?.maintenance?.count || 0})</span>
                  </div>
                  <span className="font-medium">{formatCurrency(tcoReport.tco?.breakdown?.maintenance?.total)}</span>
                </div>

                {/* Fuel */}
                <div className="flex items-center justify-between py-2 border-b">
                  <div className="flex items-center gap-2">
                    <Fuel className="w-4 h-4 text-green-500" />
                    <span>Carburant ({formatNumber(tcoReport.tco?.breakdown?.fuel?.liters)} L)</span>
                  </div>
                  <span className="font-medium">{formatCurrency(tcoReport.tco?.breakdown?.fuel?.total)}</span>
                </div>

                {/* Insurance */}
                <div className="flex items-center justify-between py-2 border-b">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-indigo-500" />
                    <span>Assurance ({tcoReport.tco?.breakdown?.insurance?.count || 0} contrats)</span>
                  </div>
                  <span className="font-medium">{formatCurrency(tcoReport.tco?.breakdown?.insurance?.total)}</span>
                </div>

                {/* Claims */}
                {(tcoReport.tco?.breakdown?.claims?.count || 0) > 0 && (
                  <div className="flex items-center justify-between py-2 border-b">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                      <span>Sinistres ({tcoReport.tco?.breakdown?.claims?.count})</span>
                    </div>
                    <span className="font-medium">{formatCurrency(tcoReport.tco?.breakdown?.claims?.total)}</span>
                  </div>
                )}

                {/* Fines */}
                {(tcoReport.tco?.breakdown?.fines?.count || 0) > 0 && (
                  <div className="flex items-center justify-between py-2 border-b">
                    <div className="flex items-center gap-2">
                      <Receipt className="w-4 h-4 text-orange-500" />
                      <span>Amendes ({tcoReport.tco?.breakdown?.fines?.count})</span>
                    </div>
                    <span className="font-medium">{formatCurrency(tcoReport.tco?.breakdown?.fines?.total)}</span>
                  </div>
                )}
              </div>

              {/* Recovery */}
              {tcoReport.tco?.totals?.disposal_recovery > 0 && (
                <div className="bg-green-50 rounded-lg p-3 mb-6">
                  <div className="flex items-center justify-between">
                    <span className="text-green-700">Valeur recuperee (vente/indemnite)</span>
                    <span className="text-lg font-bold text-green-700">
                      + {formatCurrency(tcoReport.tco?.totals?.disposal_recovery)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-green-200">
                    <span className="text-green-800 font-medium">TCO Net</span>
                    <span className="text-xl font-bold text-green-800">
                      {formatCurrency(tcoReport.tco?.totals?.net_tco)}
                    </span>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Actions */}
          <div className="flex gap-3 mt-6 pt-6 border-t">
            <button
              onClick={printReport}
              disabled={!tcoReport || loading}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FileText className="w-5 h-5" />
              Imprimer / PDF
            </button>
            <button
              onClick={downloadCSV}
              disabled={!tcoReport || loading}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Download className="w-5 h-5" />
              Exporter CSV
            </button>
          </div>

          {/* View Archive Link */}
          {archiveId && (
            <div className="text-center mt-4">
              <a
                href={`#vehicle-archives`}
                onClick={(e) => {
                  e.preventDefault();
                  onClose();
                  // Navigate to archives (this will be handled by parent)
                }}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center justify-center gap-1"
              >
                <Eye className="w-4 h-4" />
                Voir dans les archives
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
